import React, { useEffect } from 'react';
import { List } from 'immutable';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Paragraph, Node, Block } from '@uncinc/react-drupal-core';

import {
  Container,
  Button,
  useWindowDimensions,
} from '@uncinc/uncinc-react-kitchen-sink';

import {
  getMunicipalityLocations,
} from '../../../store/actions/UI';

import {
  AnchorLinks,
  Breadcrumbs,
  FadeWrapper,
  Hero,
  LatestEntitiesSmall,
  WysiwygField,
} from '../../../components';
import { locationsUrl, APP_OPTIONS, SCREEN_WIDE } from '../../../config/constants';

import './style.scss';

const TaxonomyMunicipalities = ({ entity, className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const municipalityPage = entity.toJS();

  const tid = entity.getIn(['tid', 0, 'value']);
  const name = entity.getIn(['name', 0, 'value'], '');
  const description = entity.getIn(['description', 0, 'processed'], '');
  const image = entity.getIn(['field_media_item', 0]);
  const imageTitle = entity.getIn(['field_media_item', 0, 'entity', 'field_caption', 0, 'value'], null);
  const link = entity.getIn(['field_link', 0, 'alias'], entity.getIn(['field_link', 0, 'uri']));
  const linkTitle = entity.getIn(['field_link', 0, 'title']);
  const linkTarget = entity.getIn(['field_link', 0, 'options', 'attributes', 'target'], '_self');
  const hideCTABlock = entity.getIn(['field_block_hidden', 0, 'value'], false);

  const anchorLinks = municipalityPage.field_anchor_links;
  const paragraphs = municipalityPage.field_paragraphs;

  const { width } = useWindowDimensions();
  const articlesBlock = width < SCREEN_WIDE ? 'block_6_two_articles' : 'block_3';
  const projectsBlock = width < SCREEN_WIDE ? 'block_5_two_projects' : 'block_2';

  const locations = useSelector((state) => state.getIn(['uiInternal', 'municipality_locations'], List()));
  const locationIds = locations.map((location) => {
    return location.getIn(['nid', 0, 'value']);
  });
  const currentLanguage = useSelector((state) => state.getIn(['ui', 'language']));

  useEffect(() => { dispatch(getMunicipalityLocations(tid)); }, [dispatch, tid]);

  return (
    <article typeof="schema:WebPage" className="TaxonomyMunicipalities">
      <Container>
        <div className="row">
          <div className="col-12">
            <Breadcrumbs
              data={[
                { url: locationsUrl(currentLanguage), title: t('general.municipalities') },
                { title: name },
              ]}
            />
          </div>
        </div>
      </Container>

      <Container>
        <div className="row">
          <div className="col-12 col-md-10 offset-md-1">
            <div className="Preface">
              <FadeWrapper>
                <div className="Preface__title">
                  <h1>{name}</h1>
                </div>
              </FadeWrapper>
              <FadeWrapper delay={100}>
                <div className="Preface__body">
                  <WysiwygField
                    property="schema:text"
                    html={description}
                  />
                </div>
              </FadeWrapper>

              {link && linkTitle && (
                <FadeWrapper delay={200}>
                  <div className="Preface__link">
                    <Button
                      href={link}
                      target={linkTarget}
                      className="Button--cta"
                    >
                      {linkTitle}
                    </Button>
                  </div>
                </FadeWrapper>
              )}
            </div>
          </div>
        </div>
      </Container>

      {/* Start of Hero */}
      {image && (
        <Container>
          <div className="row">
            <div className="col-12">
              <Hero
                image={image}
                description={imageTitle}
              />
            </div>
          </div>
        </Container>
      )}
      {/* End of Hero */}

      {/* Start of AnchorLinks */}
      {anchorLinks.length > 0 && (
        <Container>
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <AnchorLinks
                anchorLinks={anchorLinks}
                paragraphs={paragraphs}
              />
            </div>
          </div>
        </Container>
      )}
      {/* End of AnchorLinks */}

      {entity.get('field_paragraphs') ? (
        <div className="TaxonomyMunicipalities__paragraphs">
          {entity.get('field_paragraphs', List()).map((paragraph) => {
            const id = paragraph.get('target_id');
            return (
              <Paragraph
                key={`paragraph:${id}`}
                id={id}
                entity={paragraph.get('entity')}
              />
            );
          })}
        </div>
      ) : null}

      <Container>
        {LatestEntitiesSmall({
          blockTitle: t('general.local_vacancies'),
          block: 'block_4',
          locationIds: locationIds,
        })}
      </Container>
      <div className="TaxonomyMunicipalities__background">
        <Container>
          {LatestEntitiesSmall({
            blockTitle: t('general.local_projects'),
            block: projectsBlock,
            locationIds: locationIds,
          })}
          {LatestEntitiesSmall({
            blockTitle: t('general.local_news'),
            block: articlesBlock,
            locationIds: locationIds,
          })}
        </Container>
      </div>

      {locations.size > 0 && (
        <Container>
          <div className="row">
            <div className="col-12 heading-holder">
              <h2>{t('general.local_locations')}</h2>
            </div>
            <div className="asd">
              {locations.map((location) => {
                const id = location.getIn(['nid', 0, 'value']);
                const uuid = location.getIn(['uuid', 0, 'value']);
                return (
                  <Node
                    key={uuid}
                    id={id}
                    entity={location}
                    viewMode="teaser"
                  />
                );
              })}
            </div>
          </div>
        </Container>
      )}

      {!hideCTABlock && (
        <Container className="CTABlock">
          <div className="row">
            <div className="CTA__inner col-12 col-lg-10 offset-lg-1">
              <Block id={APP_OPTIONS.parameters.blockIdMunicipalityCTABlock} />
            </div>
          </div>
        </Container>
      )}
    </article>
  );
};

export default TaxonomyMunicipalities;
